import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {Tab} from '../../../common/tabs/tab';
import {TabsWrapper} from '../../../common/tabs/tabsWrapper';

/*
 * Constants.
 */

export enum SettingsHelpHeaderTabsEnum {
  HELP = 'help',
  ADMIN_SUGGESTIONS = 'admin_suggestions',
  PRODUCT_UPDATES = 'product_updates',
}

/*
 * Props.
 */

interface SettingsHelpHeaderProps {
  selectedTab: SettingsHelpHeaderTabsEnum;
  onSelectTab: (tab: SettingsHelpHeaderTabsEnum) => void;
  onRequestClose: () => void;
  hasAdminSuggestionsTab: boolean;
  hasProductUpdatesTab: boolean;
  hasUnseenProductUpdates: boolean;
  hasUnseenAdminSuggestions: boolean;
}

const StyledContainerDiv = styled.div`
  width: 600px;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0 30px;
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
`;

const StyledTabsWrapper = styled(TabsWrapper)`
  margin-top: 13px;
  margin-bottom: -1px; /* Overlap with the parent container's bottom border. */
`;

const StyledCloseButton = styled(ChromelessButton)`
  border-radius: 10px;

  width: 30px;
  height: 30px;

  color: ${(p) => p.theme.greys.shade80};
  ${center()}

  &:hover {
    color: ${(p) => p.theme.greys.shade70};
    background: ${(p) => p.theme.alphas.gray10};
  }
`;

/*
 * Component.
 */

export const SettingsHelpHeader: FC<SettingsHelpHeaderProps> = (props) => {
  const {
    selectedTab,
    onSelectTab,
    onRequestClose,
    hasAdminSuggestionsTab,
    hasUnseenAdminSuggestions,
    hasProductUpdatesTab,
    hasUnseenProductUpdates,
  } = props;

  return (
    <StyledContainerDiv>
      <StyledTabsWrapper>
        <Tab
          label={<Trans id="settings-help-header-help-tab-tooltip">Help</Trans>}
          isSelected={selectedTab === SettingsHelpHeaderTabsEnum.HELP}
          onClick={() => onSelectTab(SettingsHelpHeaderTabsEnum.HELP)}
        >
          <Trans id="settings-help-header-help-tab">Help</Trans>
        </Tab>
        {hasAdminSuggestionsTab && (
          <Tab
            label={<Trans id="settings-help-header-admin-suggestions-tab-tooltip">Suggestions</Trans>}
            isSelected={selectedTab === SettingsHelpHeaderTabsEnum.ADMIN_SUGGESTIONS}
            onClick={() => onSelectTab(SettingsHelpHeaderTabsEnum.ADMIN_SUGGESTIONS)}
            hasIndicator={hasUnseenAdminSuggestions}
          >
            <Trans id="settings-help-header-admin-suggestions-tab">Suggestions</Trans>
          </Tab>
        )}
        {hasProductUpdatesTab && (
          <Tab
            label={<Trans id="settings-help-header-product-updates-tab-tooltip">What’s new?</Trans>}
            isSelected={selectedTab === SettingsHelpHeaderTabsEnum.PRODUCT_UPDATES}
            onClick={() => onSelectTab(SettingsHelpHeaderTabsEnum.PRODUCT_UPDATES)}
            hasIndicator={hasUnseenProductUpdates}
          >
            <Trans id="settings-help-header-product-updates-tab">What’s new?</Trans>
          </Tab>
        )}
      </StyledTabsWrapper>

      <StyledCloseButton onClick={onRequestClose}>
        <TooltipCoordinator render={() => <Trans id="onboarding-widget-close">Close</Trans>} placement="top">
          <Icon name="close" />
        </TooltipCoordinator>
      </StyledCloseButton>
    </StyledContainerDiv>
  );
};
